import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {SelectComponent} from '../form/select/select.component';
import {SchoolType} from '../../models/schule/SchoolType';
import {forkJoin, of, Subject, takeUntil, tap} from 'rxjs';
import {SchuljahrService} from '../../@core/services/schuljahr/schuljahr.service';
import {SchuljahrDTO} from '../../models/schuljahr/SchuljahrDTO';
import {SchoolDataInfoDto} from '../../models/student-ticket/list-view/SchoolDataInfoDto';
import {SchuleService} from '../../@core/services/schule/schule.service';
import {SchuleDTO} from '../../models/schule/SchuleDTO';
import {SchuleMinimalDTO} from '../../models/schule/SchuleMinimalDTO';
import {KlasseDTO} from '../../models/klasse/KlasseDTO';
import {BildungsgangDTO} from '../../models/bildungsgang/BildungsgangDTO';

interface SchoolDetailsForm {
  schoolId: FormControl<string | null>;
  schulTyp: FormControl<string | null>;
  schoolYearId: FormControl<string | null>;
  classId: FormControl<string | null>;
  courseId: FormControl<string | null>;
}

/**
 * Wiederverwendbare Schul-Formularlogik - eingebettet sowohl im Verifizierungs-Wizard
 * (SchoolStepComponent) als auch im SchoolDetailsAdjustModalComponent.
 */
@Component({
  selector: 'app-school-details-form',
  standalone: true,
  imports: [TranslatePipe, ReactiveFormsModule, SelectComponent],
  templateUrl: './school-details-form.component.html',
})
export class SchoolDetailsFormComponent implements OnInit, OnDestroy {

  @Input() initialData: SchoolDataInfoDto | null;
  // Erlaubt unterschiedliche data-cy-Präfixe je nach Einbettungskontext (Wizard-Step vs. Modal)
  @Input() testIdPrefix = '';
  // Der Schultyp-Selector folgt in Step/Modal einer abweichenden Namenskonvention und wird daher separat parametrisiert
  @Input() schoolTypeTestId = 'schoolType';

  form: FormGroup<SchoolDetailsForm>;

  protected schuleList: SchuleMinimalDTO[] = [];
  protected schuljahreList: SchuljahrDTO[] = [];
  protected filteredSchuljahreList: SchuljahrDTO[] = [];
  protected filteredKlassenList: KlasseDTO[] = [];
  protected filteredBildungsgangList: BildungsgangDTO[] = [];
  protected schoolTypeOptions: { value: string; label: string }[] = [];

  private selectedSchule: SchuleDTO | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly schuljahrService = inject(SchuljahrService);
  private readonly schuleService = inject(SchuleService);

  ngOnInit(): void {
    this.schoolTypeOptions = Object.values(SchoolType).map(type => ({
      value: type,
      label: this.translateService.instant('TICKETDETAIL.SCHOOL_TYPE.' + type)
    }));
    this.loadLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initFormWithCurrentData());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initFormWithCurrentData(): void {
    this.applyDependentListsForSchule(this.selectedSchule);
    this.applySchuljahrFilter(this.selectedSchule);

    this.initForm();

    this.form.patchValue({
      schoolId: this.initialData?.schuleId ?? null,
      schulTyp: this.initialData?.schulTyp ?? null,
      schoolYearId: this.initialData?.schuljahrId ?? null,
      classId: this.initialData?.klasseId ?? null,
      courseId: this.initialData?.bildungsgangId ?? null,
    }, {emitEvent: false});
  }

  initForm(): void {
    this.form = this.fb.group<SchoolDetailsForm>({
      schoolId: new FormControl(null, Validators.required),
      schulTyp: new FormControl(null),
      schoolYearId: new FormControl(null, Validators.required),
      classId: new FormControl(null),
      courseId: new FormControl(null),
    });

    this.form.get('schoolId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(schuleId => {
        this.form.get('classId')?.reset(null, {emitEvent: false});
        this.form.get('courseId')?.reset(null, {emitEvent: false});
        this.form.get('schoolYearId')?.reset(null, {emitEvent: false});
        if (schuleId) {
          this.schuleService.getSchuleFull(schuleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(schule => {
              this.selectedSchule = schule;
              this.form.get('schulTyp')?.patchValue(schule.schoolType, {emitEvent: false});
              this.applyDependentListsForSchule(schule);
              this.applySchuljahrFilter(schule);
            });
        } else {
          this.selectedSchule = null;
          this.filteredKlassenList = [];
          this.filteredBildungsgangList = [];
          this.filteredSchuljahreList = [];
        }
      });
  }

  getFormItem(s: string): AbstractControl | null {
    return this.form?.get(s);
  }

  isVocational(): boolean {
    return this.form?.get('schulTyp')?.value === SchoolType.VOCATIONAL;
  }

  isValid(): boolean {
    return this.form.valid;
  }

  getFormValue(): SchoolDataInfoDto {
    return this.buildSchoolDataDto();
  }

  private buildSchoolDataDto(): SchoolDataInfoDto {
    const schuleId: string = this.getFormItem('schoolId')?.value;
    const schuljahrId: string = this.getFormItem('schoolYearId')?.value;
    const klasseId: string = this.getFormItem('classId')?.value;
    const bildungsgangId: string = this.getFormItem('courseId')?.value;

    const selectedSchuljahr = this.filteredSchuljahreList.find(s => s.schuljahrId === schuljahrId);
    const selectedKlasse = this.filteredKlassenList.find(k => k.klasseId === klasseId);
    const selectedBildungsgang = this.filteredBildungsgangList.find(b => b.bildungsgangId === bildungsgangId);

    const schulTyp: string = this.getFormItem('schulTyp')?.value;

    return {
      ...this.initialData,
      schuleId: schuleId,
      schulTyp: schulTyp ?? this.selectedSchule?.schoolType ?? this.initialData?.schulTyp,
      schuleName: this.selectedSchule?.name ?? this.initialData?.schuleName,
      schuljahrId: schuljahrId,
      schuljahrName: selectedSchuljahr?.name ?? this.initialData?.schuljahrName,
      klasseId: klasseId || null,
      klasseName: selectedKlasse?.name ?? this.initialData?.klasseName,
      bildungsgangId: bildungsgangId || null,
      bildungsgangName: selectedBildungsgang?.name ?? this.initialData?.bildungsgangName,
    };
  }

  loadLists() {
    const schulen$ = this.schuleService.getSchulenMinimalForTicket().pipe(
      tap(list => {
        this.schuleList = list;
        this.ensureCurrentSchoolInList();
      })
    );

    const currentSchuleId = this.initialData?.schuleId;
    const currentSchuleFull$ = currentSchuleId
      ? this.schuleService.getSchuleFull(currentSchuleId).pipe(
          tap(schule => { this.selectedSchule = schule; })
        )
      : of(null);

    const schuljahreAllgemein$ = this.schuljahrService.getSchuljahreBerufskolleg(true, false).pipe(
      tap(list => { this.schuljahreList = [...this.schuljahreList, ...list]; })
    );

    const schuljahreBerufskolleg$ = this.schuljahrService.getSchuljahreBerufskolleg(true, true).pipe(
      tap(list => { this.schuljahreList = [...this.schuljahreList, ...list]; })
    );

    return forkJoin([schulen$, currentSchuleFull$, schuljahreAllgemein$, schuljahreBerufskolleg$]);
  }

  private ensureCurrentSchoolInList(): void {
    const currentSchuleId = this.initialData?.schuleId;
    if (currentSchuleId && !this.schuleList.some(s => s.schuleId === currentSchuleId)) {
      const synthetic: SchuleMinimalDTO = {
        schuleId: currentSchuleId,
        name: this.initialData?.schuleName ?? '',
        id: 0,
        archiv: true,
      };
      this.schuleList = [synthetic, ...this.schuleList];
    }
  }

  private applySchuljahrFilter(schule: SchuleDTO | null): void {
    const schulTyp = schule?.schoolType ?? this.initialData?.schulTyp;
    const isVocational = schulTyp === SchoolType.VOCATIONAL;

    const aktivePassend = this.schuljahreList.filter(
      s => s.berufskolleg === isVocational && s['archiv'] !== true
    );

    const currentUuid = this.initialData?.schuljahrId;
    const currentIsArchived = currentUuid && !aktivePassend.some(s => s.schuljahrId === currentUuid);

    if (currentIsArchived) {
      const archivedCurrent = this.schuljahreList.find(s => s.schuljahrId === currentUuid);
      this.filteredSchuljahreList = archivedCurrent ? [archivedCurrent, ...aktivePassend] : aktivePassend;
    } else {
      this.filteredSchuljahreList = aktivePassend;
    }
  }

  private applyDependentListsForSchule(schule: SchuleDTO | null): void {
    const alleKlassen = schule?.klassen ?? [];
    const alleBildungsgaenge = schule?.bildungsgaenge ?? [];

    const aktiveKlassen = alleKlassen.filter(k => !k.archiv);
    const aktiveBildungsgaenge = alleBildungsgaenge.filter(b => !b.archiv);

    const currentKlasseId = this.initialData?.klasseId;
    if (currentKlasseId && !aktiveKlassen.some(k => k.klasseId === currentKlasseId)) {
      const archivedKlasse = alleKlassen.find(k => k.klasseId === currentKlasseId);
      this.filteredKlassenList = archivedKlasse ? [archivedKlasse, ...aktiveKlassen] : aktiveKlassen;
    } else {
      this.filteredKlassenList = aktiveKlassen;
    }

    const currentBgId = this.initialData?.bildungsgangId;
    if (currentBgId && !aktiveBildungsgaenge.some(b => b.bildungsgangId === currentBgId)) {
      const archivedBg = alleBildungsgaenge.find(b => b.bildungsgangId === currentBgId);
      this.filteredBildungsgangList = archivedBg ? [archivedBg, ...aktiveBildungsgaenge] : aktiveBildungsgaenge;
    } else {
      this.filteredBildungsgangList = aktiveBildungsgaenge;
    }
  }
}
